/**
 * '@palbase/web/next/client' — the browser half of the Next.js adapter. This entry
 * must NEVER import 'next' (it runs in plain client bundles); the cookie jar
 * is document.cookie itself, written with the shared codec so the server
 * side (pbServer / middleware / callback) reads the same bytes.
 */
import { environmentRefFromPublishableApiKey } from '../api-key.js';
import { __configure, getRuntime } from '../internal.js';
import type { PersistedSession, SessionStorageAdapter } from '../storage.js';
import {
  clearedSessionCookieNames,
  decodeSessionCookies,
  encodeSessionCookies,
} from './cookie-codec.js';

/** 30 days — the refresh-token TTL (P3 design contract). */
const SESSION_MAX_AGE_S = 2_592_000;

// Secure is unconditional, INCLUDING http://localhost: browsers treat
// localhost as a potentially-trustworthy origin, so document.cookie accepts
// Secure cookies there — no dev-mode special case needed. KNOWN LIMITATION:
// plain-http origins OTHER than localhost — e.g. LAN-IP device testing on
// http://192.168.x.x — are NOT trustworthy, so the browser silently DROPS
// these Secure cookie writes and the session won't persist; use https (or a
// localhost tunnel/port-forward) for on-device testing. NOT HttpOnly by
// design: the browser SDK must read/write the session (Supabase-paradigm
// tradeoff, documented in the P3 plan).
const WRITE_ATTRS = 'Path=/; SameSite=Lax; Secure';

/** Parse document.cookie ("a=1; b=2") into a name → raw-value map. */
function cookieJar(): Map<string, string> {
  const jar = new Map<string, string>();
  for (const part of document.cookie.split(';')) {
    const eq = part.indexOf('=');
    if (eq === -1) continue;
    const name = part.slice(0, eq).trim();
    if (name) jar.set(name, part.slice(eq + 1).trim());
  }
  return jar;
}

function deleteCookie(name: string): void {
  // biome-ignore lint/suspicious/noDocumentCookie: SessionStorageAdapter is a SYNC contract; the async Cookie Store API can't back it (and isn't universal).
  document.cookie = `${name}=; ${WRITE_ATTRS}; Max-Age=0`;
}

/**
 * document.cookie-backed SessionStorageAdapter. `load` returns the EXTENDED
 * PersistedSession (access token + expiry ride along) so hydration adopts
 * the full session without a refresh round-trip.
 */
export function cookieSessionStorage(environmentRef: string): SessionStorageAdapter {
  return {
    load(): PersistedSession | null {
      if (typeof document === 'undefined') return null;
      const jar = cookieJar();
      const stored = decodeSessionCookies((name) => jar.get(name), environmentRef);
      if (!stored) return null;
      // A refresh-only save round-trips as a:'' / e:0 — normalize back to
      // the legacy shape so hydration takes the expired-trick path cleanly.
      return stored.accessToken && stored.expiresAt > 0
        ? {
            refreshToken: stored.refreshToken,
            accessToken: stored.accessToken,
            expiresAt: stored.expiresAt,
          }
        : { refreshToken: stored.refreshToken };
    },
    save(session: PersistedSession): void {
      if (typeof document === 'undefined') return;
      const jar = cookieJar();
      // Delete every currently-present session cookie first (stale chunks of
      // a previously-larger session, or a stale base when the new write
      // chunks), then set the new cookie(s).
      for (const name of clearedSessionCookieNames(environmentRef, (n) => jar.has(n))) {
        deleteCookie(name);
      }
      const { set, clear } = encodeSessionCookies(environmentRef, {
        accessToken: session.accessToken ?? '',
        refreshToken: session.refreshToken,
        expiresAt: session.expiresAt ?? 0,
      });
      for (const { name, value } of set) {
        // biome-ignore lint/suspicious/noDocumentCookie: SessionStorageAdapter is a SYNC contract; the async Cookie Store API can't back it (and isn't universal).
        document.cookie = `${name}=${value}; ${WRITE_ATTRS}; Max-Age=${SESSION_MAX_AGE_S}`;
      }
      // Overflow guard (codec contract): delete one-past-the-end so a stale
      // orphan chunk behind a gap can never join a future chunk run.
      for (const name of clear) deleteCookie(name);
    },
    clear(): void {
      if (typeof document === 'undefined') return;
      const jar = cookieJar();
      for (const name of clearedSessionCookieNames(environmentRef, (n) => jar.has(n))) {
        deleteCookie(name);
      }
    },
  };
}

/**
 * One-liner for a client component/provider: re-configure the already-loaded
 * gen config (palbe.gen.ts must be imported first — throws the guided
 * notConfigured error otherwise) with cookie-backed session storage so the
 * browser and the server share the session. Calling it again simply
 * re-configures (safe, e.g. under fast refresh). When Next evaluates the
 * client component module server-side there is no document — no-op.
 */
export function setupPalbeNext(): void {
  if (typeof document === 'undefined') return;
  const config = getRuntime().config;
  __configure({
    ...config,
    storage: cookieSessionStorage(environmentRefFromPublishableApiKey(config.apiKey)),
  });
}
